import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Star, ShieldCheck, Send } from "lucide-react";
import Seo from "../components/Seo";
import { analytics } from "@heycatch/sdk";

const API = process.env.REACT_APP_BACKEND_URL || "";

const Stars = ({ value, size = "w-4 h-4" }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={size}
        strokeWidth={1.8}
        style={{
          color: i <= value ? "#39FF14" : "#3f3f46",
          fill: i <= value ? "#39FF14" : "transparent",
        }}
      />
    ))}
  </span>
);

export default function ReviewsPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData({ rows: [], summary: null }));
  }, []);

  const rows = data?.rows || [];
  const summary = data?.summary;
  const showAggregate = summary?.show_aggregate;

  const jsonLd = showAggregate
    ? {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "@id": "https://eviq.pro/#app",
        name: "EVIQ — EV Battery Intelligence",
        url: "https://eviq.pro/app",
        applicationCategory: "AutomotiveApplication",
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: String(summary.average),
          bestRating: "5",
          ratingCount: String(summary.count),
        },
        review: rows.slice(0, 10).map((r) => ({
          "@type": "Review",
          author: { "@type": "Person", name: r.name },
          datePublished: (r.created_at || "").slice(0, 10),
          reviewRating: { "@type": "Rating", ratingValue: String(r.rating), bestRating: "5" },
          reviewBody: r.body,
        })),
      }
    : null;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="reviews-page">
      <Seo
        title="EVIQ owner reviews — real ratings from EV owners"
        description="Read real, moderated reviews from Tesla, Ford, and Volvo/Polestar owners using EVIQ for battery health tracking, warranty intelligence, and Battery Passports — or leave your own."
        canonical="https://eviq.pro/reviews"
        jsonLd={jsonLd}
      />
      <div className="max-w-2xl mx-auto px-5 py-10">
        <Link
          to="/"
          data-testid="reviews-back-home"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to eviq.pro
        </Link>

        <div className="mt-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500">
            Owner reviews
          </div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-semibold leading-tight">
            What owners say about <span className="brand-text-gradient">EVIQ.</span>
          </h1>
          <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
            Every review here was written by a real person and approved by a human —
            no bots, no purchased ratings. Verified-owner badges mean the reviewer&rsquo;s
            email matches an active EVIQ subscription.
          </p>
        </div>

        {/* Aggregate summary */}
        <div
          className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5 flex items-center justify-between gap-4"
          data-testid="reviews-summary"
        >
          {summary?.count ? (
            <>
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-4xl font-semibold text-white">
                    {summary.average}
                  </span>
                  <span className="font-mono text-[11px] text-zinc-500">/ 5</span>
                </div>
                <div className="mt-1">
                  <Stars value={Math.round(summary.average)} />
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-white font-medium">
                  {summary.count} review{summary.count === 1 ? "" : "s"}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-1">
                  {showAggregate
                    ? "Shown in search results"
                    : `Rating appears in search at ${summary.min_for_aggregate}+ reviews`}
                </div>
              </div>
            </>
          ) : (
            <div className="text-sm text-zinc-400" data-testid="reviews-empty-summary">
              No approved reviews yet — be the first to rate EVIQ below. Our search-result
              rating only appears once real reviews back it.
            </div>
          )}
        </div>

        {/* Review list */}
        <div className="mt-6 space-y-3" data-testid="reviews-list">
          {data === null ? (
            <div className="text-xs text-zinc-500 font-mono">Loading reviews…</div>
          ) : rows.length === 0 ? (
            <div
              className="rounded-2xl border border-dashed border-white/15 p-8 text-center"
              data-testid="reviews-empty-state"
            >
              <Star className="w-6 h-6 mx-auto text-zinc-600" strokeWidth={1.6} />
              <div className="mt-3 text-white font-medium text-sm">Be the first to review EVIQ</div>
              <p className="mt-1 text-xs text-zinc-500 leading-relaxed max-w-sm mx-auto">
                Used EVIQ to track your pack, prep a warranty claim, or sell your EV?
                Tell the next owner what to expect — your review goes live after a quick
                human check.
              </p>
            </div>
          ) : (
            rows.map((r) => (
              <div
                key={r.id}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
                data-testid="review-card"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-white font-medium truncate">{r.name}</span>
                    {r.verified_owner && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full border border-[#00E5FF]/40 bg-[#00E5FF]/[0.07] px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-[#00E5FF] shrink-0"
                        data-testid="review-verified-badge"
                      >
                        <ShieldCheck className="w-3 h-3" strokeWidth={2.2} /> Verified owner
                      </span>
                    )}
                  </div>
                  <Stars value={r.rating} size="w-3.5 h-3.5" />
                </div>
                <div className="mt-1 font-mono text-[10px] text-zinc-500">
                  {r.vehicle ? `${r.vehicle} · ` : ""}
                  {new Date(r.created_at).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
                <p className="mt-3 text-sm text-zinc-300 leading-relaxed">{r.body}</p>
              </div>
            ))
          )}
        </div>

        <ReviewForm />
      </div>
    </div>
  );
}

const ReviewForm = () => {
  const [form, setForm] = useState({ name: "", email: "", vehicle: "", rating: 0, body: "" });
  const [phase, setPhase] = useState("idle"); // idle | busy | done | dup
  const [error, setError] = useState("");
  const [fromInvite, setFromInvite] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const r = parseInt(params.get("r") || "", 10);
    const e = params.get("e") || "";
    const n = params.get("n") || "";
    if (r >= 1 && r <= 5) {
      setForm((f) => ({ ...f, rating: r, email: e || f.email, name: n || f.name }));
      setFromInvite(true);
      setTimeout(() => {
        document.querySelector('[data-testid="review-form"]')?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.rating) return setError("Pick a star rating.");
    if (form.body.trim().length < 20) return setError("Tell us a bit more — at least 20 characters.");
    setPhase("busy");
    try {
      const r = await fetch(`${API}/api/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          vehicle: form.vehicle.trim() || null,
          rating: Number(form.rating),
          body: form.body.trim(),
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.detail?.[0]?.msg || j?.detail || "Could not submit — try again.");
      if (!j.already_submitted) analytics.trackEvent("review_submitted", { rating: Number(form.rating) });
      setPhase(j.already_submitted ? "dup" : "done");
    } catch (e2) {
      setPhase("idle");
      setError(e2.message || "Could not submit — try again.");
    }
  };

  if (phase === "done" || phase === "dup") {
    return (
      <div
        className="mt-10 rounded-2xl border border-[#39FF14]/30 bg-[#39FF14]/[0.05] p-6 text-center"
        data-testid="review-form-success"
      >
        <ShieldCheck className="w-6 h-6 mx-auto" style={{ color: "#39FF14" }} strokeWidth={2} />
        <div className="mt-2 text-white font-medium text-sm">
          {phase === "dup" ? "You've already submitted a review — thank you!" : "Thanks — review received."}
        </div>
        {phase === "done" && (
          <p className="mt-1 text-xs text-zinc-400">
            A human will read it shortly. Once approved, it appears right here.
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-10" data-testid="review-form">
      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500">
        Write a review
      </div>
      <h2 className="font-display mt-2 text-xl font-semibold text-white">
        Used EVIQ? Rate it.
      </h2>
      <p className="mt-1 text-xs text-zinc-500 leading-relaxed">
        Your email stays private — it&rsquo;s only used to mark verified owners and is never shown.
        Reviews publish after human approval.
      </p>
      {fromInvite && (
        <div
          className="mt-3 rounded-lg border border-[#39FF14]/30 bg-[#39FF14]/[0.05] px-3 py-2 text-[12px] text-zinc-300"
          data-testid="review-invite-banner"
        >
          Thanks for tapping through from your invite — your rating is pre-filled below. Add a few
          words and hit submit.
        </div>
      )}

      <div className="mt-5 space-y-3">
        <div className="flex items-center gap-2" data-testid="review-rating-picker">
          {[1, 2, 3, 4, 5].map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => setForm((f) => ({ ...f, rating: i }))}
              data-testid={`review-star-${i}`}
              aria-label={`${i} star${i === 1 ? "" : "s"}`}
              className="p-1 transition-transform hover:scale-110"
            >
              <Star
                className="w-7 h-7"
                strokeWidth={1.6}
                style={{
                  color: i <= form.rating ? "#39FF14" : "#52525b",
                  fill: i <= form.rating ? "#39FF14" : "transparent",
                }}
              />
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            type="text"
            required
            minLength={2}
            maxLength={60}
            value={form.name}
            onChange={set("name")}
            placeholder="Your name"
            data-testid="review-input-name"
            className="w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14]/50"
          />
          <input
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="you@example.com (kept private)"
            data-testid="review-input-email"
            className="w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14]/50"
          />
        </div>
        <input
          type="text"
          maxLength={80}
          value={form.vehicle}
          onChange={set("vehicle")}
          placeholder="Your EV (e.g., 2022 Model Y Long Range) — optional"
          data-testid="review-input-vehicle"
          className="w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14]/50"
        />
        <textarea
          required
          minLength={20}
          maxLength={1200}
          rows={4}
          value={form.body}
          onChange={set("body")}
          placeholder="What did EVIQ help you do? Battery health check, warranty claim, resale…"
          data-testid="review-input-body"
          className="w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14]/50 resize-y"
        />
        {error && (
          <div className="text-[12px] text-[#FF6B57]" data-testid="review-form-error">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={phase === "busy"}
          data-testid="review-form-submit"
          className="brand-gradient text-black font-semibold rounded-full px-6 py-3 text-sm inline-flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {phase === "busy" ? "Submitting…" : "Submit review"}
          <Send className="w-3.5 h-3.5" strokeWidth={2.4} />
        </button>
      </div>
    </form>
  );
};
